'use client'

import { useState } from 'react'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { formatDateString } from '@/lib/date'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { Calendar } from '@/ui/calendar'
import {
  HeartIcon,
  UsersIcon,
  BriefcaseIcon,
  CalendarBlankIcon,
  XIcon,
  LinkIcon,
} from '@phosphor-icons/react'
import { useUpdatePerson } from '@/entries'
import { cn } from '@/lib/tailwind'
import type { Entry } from '@/document'
import type { Database } from '@/account'
import { LinkPersonDialog, TrustToggle } from '@/social'

type PersonCategory = Database['public']['Enums']['person_category']

const CATEGORY_OPTIONS: {
  value: PersonCategory
  label: string
  Icon: typeof HeartIcon
}[] = [
  { value: 'family', label: 'Family', Icon: HeartIcon },
  { value: 'friend', label: 'Friend', Icon: UsersIcon },
  { value: 'work', label: 'Work', Icon: BriefcaseIcon },
]

interface EditPersonDialogProps {
  /** The person entry to edit */
  person: Entry
  /** Trigger element */
  trigger: React.ReactNode
  /** Called after successfully updating the person */
  onSuccess?: () => void
}

/**
 * A dialog for editing an existing person's details.
 */
export function EditPersonDialog({
  person,
  trigger,
  onSuccess,
}: EditPersonDialogProps) {
  const metadata = person.metadata as {
    category?: PersonCategory
    birthday?: string
    linkedIdentityId?: string
    linkedHandle?: string
  }
  const [open, setOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [name, setName] = useState(person.title)
  const [category, setCategory] = useState<PersonCategory>(
    metadata.category || 'friend',
  )
  const [birthday, setBirthday] = useState<Date | undefined>(
    metadata.birthday ? new Date(metadata.birthday) : undefined,
  )
  const [notes, setNotes] = useState(person.content || '')
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const updatePerson = useUpdatePerson()

  // Reset form when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (newOpen) {
      // Reset form to current person values when opening
      const personMetadata = person.metadata as {
        category?: PersonCategory
        birthday?: string
      }
      setName(person.title)
      setCategory(personMetadata.category || 'friend')
      setBirthday(
        personMetadata.birthday ? new Date(personMetadata.birthday) : undefined,
      )
      setNotes(person.content || '')
      setError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    setError(null)

    updatePerson.mutate(
      {
        id: person.id,
        data: {
          name: name.trim(),
          category,
          birthday: birthday ? formatDateString(birthday) : null,
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          handleOpenChange(false)
          onSuccess?.()
        },
        onError: (err) => {
          setError(
            err instanceof Error ? err.message : 'Failed to update person',
          )
        },
      },
    )
  }

  const formatBirthdayDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const hasChanges =
    name !== person.title ||
    category !== metadata.category ||
    (birthday ? formatDateString(birthday) : null) !== metadata.birthday ||
    (notes.trim() || null) !== (person.content || null)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Person</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="edit-person-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="edit-person-name"
              placeholder="e.g., Sarah Chen"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (error) setError(null)
              }}
              disabled={updatePerson.isPending}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Category</label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as PersonCategory)}
              disabled={updatePerson.isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex items-center gap-2">
                      <opt.Icon className="h-4 w-4" />
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Birthday{' '}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={updatePerson.isPending}
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !birthday && 'text-muted-foreground',
                  )}
                >
                  <CalendarBlankIcon className="mr-2 h-4 w-4" />
                  {birthday ? formatBirthdayDisplay(birthday) : 'Select date'}
                  {birthday && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setBirthday(undefined)
                      }}
                      className="text-muted-foreground hover:text-foreground ml-auto rounded-sm p-0.5"
                      aria-label="Clear birthday"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={birthday}
                  onSelect={(date) => {
                    setBirthday(date)
                    setCalendarOpen(false)
                  }}
                  captionLayout="dropdown"
                  fromYear={1920}
                  toYear={new Date().getFullYear()}
                  defaultMonth={birthday || new Date(2000, 0)}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="edit-person-notes" className="text-sm font-medium">
              Notes{' '}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </label>
            <Textarea
              id="edit-person-notes"
              placeholder="Add notes about this person..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={updatePerson.isPending}
              className="min-h-20 resize-none"
            />
          </div>

          {/* Social Link Section */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Konstantout Link{' '}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </label>
            {metadata.linkedIdentityId ? (
              <div className="flex items-center gap-2">
                <span className="bg-muted inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm">
                  <LinkIcon className="h-3.5 w-3.5" />@{metadata.linkedHandle}
                </span>
                <TrustToggle userId={metadata.linkedIdentityId} />
              </div>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLinkDialogOpen(true)}
                  className="gap-1.5"
                >
                  <LinkIcon className="h-3.5 w-3.5" />
                  Link to Konstantout
                </Button>
                <LinkPersonDialog
                  open={linkDialogOpen}
                  onOpenChange={setLinkDialogOpen}
                  personId={person.id}
                  personName={person.title}
                />
              </>
            )}
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={updatePerson.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updatePerson.isPending || !name.trim() || !hasChanges}
            >
              {updatePerson.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
